// Lifted from KPI Beast `src/lib/engine/team-structure.ts` (`agency` 8cd3387, the deployed engine;
// `codex/team-enclosures` e19db30 is identical), with its test `tests/team-structure.test.ts`
// unchanged but for the import. Logic unchanged (Biome formatting, strict-TS non-null
// assertions). CompanyOS uses `validateTeamStructure` for team containment (teams.ts here);
// `validateTeamPlacement` is kept as KPI Beast's advisory shape rule. See ../README.md.
/** Organizational containment and placement within personal reporting lines. */
export interface TeamStructure {
  id: string;
  kind: string;
  parentTeamId: string | null;
  leadSeatId: string | null;
  membershipMode: string;
  memberIds: string[];
}

export function validateTeamStructure(team: TeamStructure, teams: TeamStructure[]): string | null {
  if (team.membershipMode === "branch" && !team.leadSeatId)
    return "Choose a lead for reporting-branch membership, or select members without a lead";
  if (team.leadSeatId && !team.memberIds.includes(team.leadSeatId))
    return "The lead must be a member of this group";
  if (team.kind === "department" || team.kind === "cohort") {
    return team.parentTeamId ? "Departments and coaching cohorts must be root groups" : null;
  }
  if (!team.parentTeamId) return null;
  const byId = new Map(teams.map((t) => [t.id, t]));
  const seen = new Set([team.id]);
  let parentId: string | null = team.parentTeamId;
  while (parentId) {
    if (seen.has(parentId)) return "A team hierarchy cannot contain a cycle";
    seen.add(parentId);
    const parent = byId.get(parentId);
    if (!parent || parent.kind === "cohort") return "Choose a valid parent department or team";
    if (team.memberIds.some((id) => !parent.memberIds.includes(id)))
      return "Every member must belong to the parent department or team";
    if (parent.kind === "department") return null;
    parentId = parent.parentTeamId;
  }
  return null;
}

export interface ReportingSeat {
  id: string;
  parentSeatId: string | null;
  seatType?: string;
}
type Placement = Pick<TeamStructure, "kind" | "membershipMode" | "leadSeatId" | "memberIds">;
export function samePeerGroup(a: ReportingSeat, b: ReportingSeat): boolean {
  return (
    a.id === b.id ||
    (a.parentSeatId !== null &&
      a.parentSeatId === b.parentSeatId &&
      (a.seatType === "assistant") === (b.seatType === "assistant"))
  );
}

/** Horizontal teams are siblings under one manager, not an arbitrary depth match. */
export function validateTeamPlacement(team: Placement, seats: ReportingSeat[]): string | null {
  if (team.kind === "cohort") return null; // Coaching cohorts are not org-chart teams.
  const members = [...new Set(team.memberIds)].map((id) => seats.find((s) => s.id === id));
  if (!members.length) return "Choose at least one member";
  if (members.some((s) => !s)) return "Choose active seats in the reporting chart";
  if (team.kind === "department" && team.membershipMode !== "branch")
    return "A department must contain a manager and the entire reporting branch";
  if (team.membershipMode === "branch") {
    if (!team.leadSeatId || !seats.some((s) => s.id === team.leadSeatId))
      return "Choose the manager responsible for this reporting branch";
    const expected = new Set<string>();
    const pending = [team.leadSeatId];
    while (pending.length) {
      const id = pending.pop()!;
      if (expected.has(id)) continue;
      expected.add(id);
      pending.push(...seats.filter((s) => s.parentSeatId === id).map((s) => s.id));
    }
    if (expected.size !== members.length || members.some((s) => !expected.has(s!.id)))
      return "Include the manager and the entire reporting branch, including assistants";
    return null;
  }
  const first = members[0]!;
  if (
    members.some(
      (s) => s!.id !== first.id && (!first.parentSeatId || s!.parentSeatId !== first.parentSeatId),
    )
  )
    return "Team members must report to the same manager. For cross-department work, create a separate project manager and reporting branch.";
  if (members.some((s) => !samePeerGroup(first, s!)))
    return "Choose peers at the same level; assistants and direct reports form different peer groups.";
  return null;
}
